<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { transformCodeToDate, getZoneLabel } from '@/functions/utils';
import { fetchCultureDetails, fetchLightCultures } from '@/functions/fetch_tables';
import { groupedCultures } from '@/functions/functions_cultures';

import { useToast } from 'vue-toastification'
import { useDataStore } from '@/stores/useDataStore';
import { useAuthStore } from '@/stores/useAuthStore';

const authStore = useAuthStore();
const toast = useToast()
const dataStore = useDataStore();

import { useI18n } from 'vue-i18n';
const { t } = useI18n();

async function selectCulture(cultureId: number) {
  try {
    const cultureDetails = await fetchCultureDetails(cultureId);
    dataStore.selectedCulture = cultureDetails;
    dataStore.showCreateCulture = false;
  } catch (error) {
    console.error(t('errorLoadingCultureDetails'), ": ", error);
    toast.error(t('errorLoadingCultureDetails'))

  }
}

// Charger les données au montage
onMounted(async () => {
  await dataStore.loadBasicData();
  await fetchLightCultures();
});

// Surveiller selectedCulture dans le store et calculer enrichedCulture
watch(() => dataStore.selectedCulture, (newVal: Culture | null) => {
  if (newVal) {
    const today = new Date();
    const startDateStr = transformCodeToDate(newVal.startdaycode.startday_code);
    const startDate = new Date(startDateStr);
    const age_in_days = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    dataStore.enrichedCulture = {
      ...newVal,
      age_in_days,
      number_of_harvests: newVal.recoltes?.length || 0,
      number_of_samples: newVal.prelevements?.length || 0,
    };
  } else {
    dataStore.enrichedCulture = null;
  }
});

// Réinitialiser la sélection si filtres changent
watch([() => dataStore.filtreEspece, () => dataStore.filtrePBR], () => {
  dataStore.selectedCulture = null;
});
</script>

<template>
  <div>
    <div v-if="authStore.scopes.includes('reader')">

      <div style="width: 100%;">
        <h3>{{ $t('culturesTitle') }}</h3>
        <div>
          <label>
            <input v-model="dataStore.showInactive" type="checkbox" class="styled-checkbox" />
            {{ $t('showInactiveCultures') }}
          </label>
        </div>

        <div class="dropdown-container">
          <label>
            {{ $t('speciesLabel') }}
            <select v-model="dataStore.filtreEspece" class="styled-dropdown">
              <option :value="null">{{ $t('allSpeciesOption') }}</option>
              <option v-for="specie in dataStore.species" :value="specie.id" :key="specie.id">
                {{ specie.specie_code }}
              </option>
            </select>
          </label>
          <label>
            {{ $t('pbrLabel') }}
            <select v-model="dataStore.filtrePBR" class="styled-dropdown">
              <option :value="null">{{ $t('allPBROption') }}</option>
              <option v-for="pbr in dataStore.pbrs" :value="pbr.id" :key="pbr.id">
                {{ pbr.numero_pbr }}
              </option>
            </select>
          </label>
        </div>

        <div v-for="(cultures, zone) in groupedCultures" :class="['zone-block', `${zone}`]" :key="zone">
          <h3>{{ getZoneLabel(zone as 'zoneA' | 'zoneB' | 'zoneC') }}</h3>
          <div class="button-group">
            <button v-for="culture in cultures" :class="{
              active: dataStore.selectedCulture?.id === culture.id,
              off: !culture.active,
              on: culture.active
            }" @click="() => selectCulture(culture.id)" :key="culture.id">
              {{ culture.pbr.numero_pbr }} | {{ culture.specie.specie_code }} | {{ culture.startdaycode.startday_code }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped></style>
